import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Film, PlayCircle, Loader2 } from "lucide-react";
import { deleteMovie, listMovies, videoUrl } from "@/lib/api";

const EMPTY_IMG =
  "https://images.unsplash.com/photo-1723723467478-eb4667116137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb3ZpZSUyMGNsYXBwZXJib2FyZCUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4OTA5NTY0OXww&ixlib=rb-4.1.0&q=85";

const STATUS_COPY = {
  queued: "In queue",
  scripting: "Drafting screenplay",
  generating_video: "Rendering with Flux + Ken Burns",
  generating_voiceover: "Recording voiceover",
  complete: "Ready",
  failed: "Failed",
};

export default function Gallery() {
  const [movies, setMovies] = useState(null);

  const load = async () => {
    try {
      const data = await listMovies();
      setMovies(data);
    } catch (e) {
      toast.error("Failed to load your reels");
      setMovies([]);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const onDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteMovie(id);
      toast.success("Reel removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-10 md:py-24">
      <div className="mb-14 flex items-end justify-between border-b border-white/10 pb-8">
        <div>
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/40">
            / Your Reels · Collection
          </div>
          <h1 className="mt-3 font-display text-5xl font-bold leading-none tracking-tight md:text-7xl">
            The screening room.
          </h1>
        </div>
        <Link
          to="/"
          data-testid="new-reel-cta"
          className="hidden border border-[#E50914] bg-[#E50914] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.28em] text-white transition-colors duration-200 hover:bg-transparent md:inline-block"
        >
          + New Reel
        </Link>
      </div>

      {movies === null && (
        <div className="flex justify-center py-24 text-white/60">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {movies && movies.length === 0 && (
        <div
          data-testid="empty-state"
          className="relative overflow-hidden border border-white/10"
        >
          <img src={EMPTY_IMG} alt="clapperboard" className="h-[420px] w-full object-cover opacity-60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-[#050505] via-black/70 to-transparent p-10 text-center">
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#E50914]">
              scene 01 · take 01
            </div>
            <h3 className="mt-3 font-display text-4xl font-bold md:text-5xl">Nothing on the reel yet.</h3>
            <p className="mt-3 max-w-md text-sm text-white/60">
              Head to the studio, type a sentence, and let the crew do the rest.
            </p>
            <Link
              to="/"
              data-testid="empty-cta"
              className="mt-8 border border-[#E50914] bg-[#E50914] px-8 py-3 font-mono-ui text-xs uppercase tracking-[0.32em] text-white hover:bg-transparent"
            >
              Enter Studio
            </Link>
          </div>
        </div>
      )}

      {movies && movies.length > 0 && (
        <div
          data-testid="gallery-grid"
          className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {movies.map((m) => (
            <Link
              to={`/movie/${m.id}`}
              key={m.id}
              data-testid={`movie-card-${m.id}`}
              className="group relative block border border-white/10 bg-[#0A0A0A] transition-all duration-300 hover:border-white/40"
            >
              <div className="relative aspect-video overflow-hidden bg-black">
                {m.has_video ? (
                  <>
                    <video
                      src={videoUrl(m.id)}
                      poster={m.has_poster ? `${videoUrl(m.id).replace("/video", "/poster.jpg")}` : undefined}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <PlayCircle className="h-14 w-14 text-white" strokeWidth={1.2} />
                    </div>
                  </>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#0A0A0A]">
                    {m.status === "failed" ? (
                      <Film className="h-10 w-10 text-white/20" strokeWidth={1} />
                    ) : (
                      <Loader2 className="h-8 w-8 animate-spin text-[#E50914]" strokeWidth={1.5} />
                    )}
                    <div className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/50">
                      {STATUS_COPY[m.status] || m.status}
                    </div>
                  </div>
                )}
                <button
                  data-testid={`delete-${m.id}`}
                  onClick={(e) => onDelete(m.id, e)}
                  className="absolute right-3 top-3 border border-white/20 bg-black/60 p-2 text-white/70 backdrop-blur-md transition-colors duration-200 hover:border-[#E50914] hover:text-[#E50914]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#E50914]">
                    {m.genre}
                  </span>
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/40">
                    {(m.num_scenes || 1)}×{m.duration}s · {m.size}
                  </span>
                </div>
                <h3 className="mt-3 line-clamp-2 font-display text-2xl font-bold leading-tight tracking-tight">
                  {m.title || "Untitled Reel"}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-white/50">
                  {m.logline || m.prompt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
